/**
 * Versioned Terms-of-Service registry.
 *
 * Each entry represents a publicly-archived version of /terms. The newest
 * entry MUST be first; the page reads `TERMS_VERSIONS[0]` as the current
 * version. Older entries stay available so users can verify which wording
 * applied at the time they accepted — the same transparency model lovable.dev
 * uses.
 *
 * To publish a new version:
 *   1. Add a new entry at the top of the array with a fresh `id`
 *      (`YYYY-MM-DD`), `effectiveDate` and a short `summary` describing
 *      what changed.
 *   2. Optionally point `messageNamespace` at a snapshotted i18n namespace
 *      (e.g. `legal.terms_2026_05_01`) if wording diverges from the current
 *      draft. Until snapshots exist, all entries share `legal.terms` and
 *      changes are tracked in compliance Notion.
 */

export type TermsVersion = {
  /** Stable URL slug; surfaced as `?v={id}`. */
  id: string;
  /** ISO date when this version becomes binding. Used for the dropdown label
   * (formatted via `useFormatter` so each locale gets its own date format). */
  effectiveDate: string;
  /** Optional i18n namespace override; defaults to `legal.terms`. */
  messageNamespace?: string;
};

export const TERMS_VERSIONS: readonly TermsVersion[] = [
  {
    id: '2026-05-01',
    effectiveDate: '2026-05-01',
  },
] as const;

export const CURRENT_TERMS_VERSION = TERMS_VERSIONS[0];

/**
 * H3 (Audit) — Stable string identifier of the currently-binding ToS
 * version. Persisted into `tos_acceptances.version` and compared by
 * `useTosAcceptance` against the user's latest acceptance row.
 *
 * This is intentionally just the `id` of `CURRENT_TERMS_VERSION` (a
 * `YYYY-MM-DD` slug) so it serialises cleanly into the database and
 * the API surface — no need for object equality across hydration
 * boundaries.
 */
export const CURRENT_TOS_VERSION: string = CURRENT_TERMS_VERSION.id;

export function findTermsVersion(id: string | null | undefined): TermsVersion {
  if (!id) return CURRENT_TERMS_VERSION;
  return TERMS_VERSIONS.find((v) => v.id === id) ?? CURRENT_TERMS_VERSION;
}
